import { apiClient } from '../../core/api/client';

export interface PlanCapabilities {
  autoText: boolean;
  autoImage: boolean;
  autoTts: boolean;
}

export interface ServerPlan {
  id: string;
  name: string;
  priceCents: number;
  durationDays: number;
  summary: string;
  tags: string[];
  description: string;
  capabilities: PlanCapabilities;
  quotaEnabled: boolean;
  textTokenQuota: number;
  imageQuota: number;
  ttsCharQuota: number;
  listed: boolean;
  sortOrder: number;
  createdAt?: string;
  updatedAt?: string;
  archivedAt?: string | null;
}

export interface ActivePlanSubscription {
  id: string;
  planId: string;
  planName: string;
  priceCents: number;
  durationDays: number;
  startsAt: string;
  expiresAt: string;
}

export interface QuotaCounter {
  limit: number | null;
  used: number;
  remaining: number | null;
}

export interface EntitlementSnapshot {
  active: boolean;
  expiresAt: string | null;
  subscriptions: ActivePlanSubscription[];
  capabilities: PlanCapabilities;
  quota: {
    textTokens: QuotaCounter;
    images: QuotaCounter;
    ttsChars: QuotaCounter;
  };
}

export interface PlanPurchaseResult {
  subscription: ActivePlanSubscription;
  balanceUnits: number;
}

export const EMPTY_ENTITLEMENTS: EntitlementSnapshot = {
  active: false,
  expiresAt: null,
  subscriptions: [],
  capabilities: { autoText: false, autoImage: false, autoTts: false },
  quota: {
    textTokens: { limit: 0, used: 0, remaining: 0 },
    images: { limit: 0, used: 0, remaining: 0 },
    ttsChars: { limit: 0, used: 0, remaining: 0 },
  },
};

export async function listPublicPlans(): Promise<ServerPlan[]> {
  return (await apiClient.get<{ plans: ServerPlan[] }>('/v1/plans')).plans;
}

export async function loadSubscriptionSnapshot(): Promise<EntitlementSnapshot> {
  return apiClient.get<EntitlementSnapshot>('/v1/subscription');
}

export async function purchasePlanWithBalance(
  planId: string,
  idempotencyKey: string,
): Promise<PlanPurchaseResult> {
  return apiClient.post<PlanPurchaseResult>(
    `/v1/plans/${encodeURIComponent(planId)}/purchase`,
    { idempotencyKey, paymentMethod: 'balance' },
  );
}

export function formatPlanPrice(priceCents: number): string {
  if (!Number.isFinite(priceCents) || priceCents < 0) return '价格异常';
  return `￥${(Math.floor(priceCents) / 100).toFixed(2)}`;
}

export function quotaLabel(counter: QuotaCounter): string {
  if (counter.limit === null) return '不限量';
  return `${Math.max(0, counter.used).toLocaleString()} / ${counter.limit.toLocaleString()}`;
}
